import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { cn } from '../../../lib/utils';
import { AssigneeAvatar } from '../../hubspot-management/table/HubspotTicketCells';
import type { HubspotTicketDetailData } from '../types';

interface HubspotTicketAssigneeControlProps {
  assignedUser: HubspotTicketDetailData['assignedUser'] | null;
  assignableUsers: HubspotTicketDetailData['assignableUsers'];
  selectedAssignedUserId: string;
  isSaving: boolean;
  error: string | null;
  canEdit: boolean;
  onChange: (userId: string) => void;
}

/** Assignee as a chip; editable chips save on change. */
export function HubspotTicketAssigneeControl({
  assignedUser,
  assignableUsers,
  selectedAssignedUserId,
  isSaving,
  error,
  canEdit,
  onChange,
}: HubspotTicketAssigneeControlProps) {
  const avatar = (
    <AssigneeAvatar
      name={assignedUser?.fullName ?? null}
      className="size-5 text-[9px]"
    />
  );

  if (!canEdit) {
    return (
      <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900/60 pl-1 pr-2 text-xs text-neutral-300">
        {avatar}
        {assignedUser?.fullName ?? 'Unassigned'}
      </span>
    );
  }

  return (
    <div className="inline-flex flex-col">
      <div
        className={cn(
          'inline-flex h-9 items-center rounded-md border bg-neutral-900/60 pl-1 text-base sm:h-7 sm:text-xs',
          error ? 'border-red-500/40' : 'border-neutral-800',
        )}
      >
        {avatar}
        <div className="relative">
          <select
            aria-label="Assignee"
            value={selectedAssignedUserId}
            onChange={(event) => onChange(event.target.value)}
            disabled={isSaving}
            className="h-full max-w-[12rem] cursor-pointer appearance-none truncate bg-transparent pl-1.5 pr-6 text-white focus:outline-none disabled:cursor-wait disabled:opacity-60"
            style={{ colorScheme: 'dark' }}
          >
            <option value="">Unassigned</option>
            {assignableUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullName} (
                {user.role === 'superadmin' ? 'admin' : user.role})
              </option>
            ))}
          </select>
          <ExpandMoreIcon className="pointer-events-none absolute right-1 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
        </div>
        {isSaving && (
          <span
            aria-label="Saving assignment"
            className="mr-2 size-3 animate-spin rounded-full border-2 border-teal-400 border-t-transparent"
          />
        )}
      </div>
      {error && <span className="mt-1 text-xs text-red-400">{error}</span>}
    </div>
  );
}
